import { useEffect, useRef, useState } from "react";

import { api, Place } from "../api";

/** Debounced pool search with keyboard navigation. */
export function PlaceSearch({
  placeholder = "Search a place…",
  onPick,
  autoFocus = false,
  arena,
}: {
  placeholder?: string;
  onPick: (place: Place) => void;
  autoFocus?: boolean;
  /** Restrict results to the declared board, so nothing off it is offerable. */
  arena?: string;
}) {
  const [query, setQuery] = useState("");
  const [places, setPlaces] = useState<Place[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const latest = useRef(0);

  useEffect(() => {
    if (query.trim().length < 2) {
      setPlaces([]);
      return;
    }
    const seq = ++latest.current;
    const timer = setTimeout(async () => {
      try {
        const { places: found } = await api.search(query, 10, arena);
        // Ignore a response that a newer keystroke has already superseded.
        if (seq === latest.current) {
          setPlaces(found);
          setActive(0);
          setOpen(true);
        }
      } catch {
        if (seq === latest.current) setPlaces([]);
      }
    }, 160);
    return () => clearTimeout(timer);
  }, [query, arena]);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const choose = (place: Place) => {
    onPick(place);
    setQuery("");
    setPlaces([]);
    setOpen(false);
  };

  return (
    <div className="guess-box" ref={box}>
      <input
        value={query}
        placeholder={placeholder}
        autoFocus={autoFocus}
        aria-label={placeholder}
        aria-autocomplete="list"
        aria-expanded={open && places.length > 0}
        role="combobox"
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => places.length && setOpen(true)}
        onKeyDown={(e) => {
          // Enter commits whenever there are results, even if the list was
          // dismissed -- gating it on `open` makes the keyboard path feel
          // broken for anyone not using a mouse.
          if (places.length === 0) return;
          if (e.key === "Enter") {
            e.preventDefault();
            choose(places[active]);
            return;
          }
          if (!open) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => (i + 1) % places.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => (i - 1 + places.length) % places.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            choose(places[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open && places.length > 0 && (
        <div className="results" role="listbox">
          {places.map((place, i) => (
            <button
              key={place.id}
              className="result-row"
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(place)}
            >
              <div>{place.name}</div>
              <div className="result-sub">
                {[place.admin1, place.countryName].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
